// The "Resources" backpack. The trigger is a little backpack FAB; opening it
// reveals a backpack whose contents are SCATTERED like real objects stuffed
// inside. Tapping an object animates a detail popup with everything the student
// needs to know. Shared by the Welcome Day viewer and its editor preview.
import { useEffect, useRef, useState } from 'react';
import { picUrl } from './placeImages.js';
import RichText from './RichText.jsx';

const DONE_KEY = 'umaps-wd-done'; // which resources the student has ticked off
const loadDone = () => { try { return JSON.parse(localStorage.getItem(DONE_KEY)) || {}; } catch { return {}; } };

// Preset scatter spots (centre position + tilt) so items look tossed into the
// bag. Used when a resource has no saved `place`.
const SPOTS = [
  { x: 22, y: 20, rot: -9 },
  { x: 66, y: 22, rot: 11 },
  { x: 32, y: 48, rot: 5 },
  { x: 70, y: 52, rot: -13 },
  { x: 24, y: 76, rot: 8 },
  { x: 68, y: 74, rot: -6 },
  { x: 50, y: 38, rot: 3 },
  { x: 46, y: 66, rot: -4 },
];
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
// The scatter spot (or saved placement) for a resource, as {x,y,scale,rot}.
const placeOf = (r, i) => r.place || { ...SPOTS[i % SPOTS.length], scale: 1 };

export default function WelcomeBackpack({
  resources = [], variant = 'fab', open: openProp, onOpenChange, label,
  // Editor placement mode: drag to move, drag handles to scale/rotate.
  editable = false, onPlace, selectedId = null, onSelect,
  // Viewer: wayfind to a resource's pickup location from its detail card.
  onGo,
}) {
  // Controlled (parent owns `open`) when onOpenChange is provided, else internal.
  const controlled = typeof onOpenChange === 'function';
  const [openState, setOpenState] = useState(false);
  const open = controlled ? !!openProp : openState;
  const setOpen = (v) => {
    const nv = typeof v === 'function' ? v(open) : v;
    if (controlled) onOpenChange(nv); else setOpenState(nv);
  };
  const [detail, setDetail] = useState(null);
  const [done, setDone] = useState(loadDone);

  const toggleDone = (e, id) => {
    e?.stopPropagation();
    setDone(d => { const nd = { ...d, [id]: !d[id] }; try { localStorage.setItem(DONE_KEY, JSON.stringify(nd)); } catch { /* ignore */ } return nd; });
  };

  // ── Placement dragging (editor only) ───────────────────────────────────────
  const pocketRef = useRef(null);
  const dragRef = useRef(null); // { mode, id, place, rect, offX, offY, baseDist, baseScale }
  useEffect(() => {
    if (!editable) return;
    const onMove = (e) => {
      const d = dragRef.current; if (!d) return;
      e.preventDefault();
      const r = d.rect;
      const px = ((e.clientX - r.left) / r.width) * 100;
      const py = ((e.clientY - r.top) / r.height) * 100;
      const cx = r.left + (d.place.x / 100) * r.width;
      const cy = r.top + (d.place.y / 100) * r.height;
      if (d.mode === 'move') {
        onPlace?.(d.id, { ...d.place, x: clamp(px + d.offX, 4, 96), y: clamp(py + d.offY, 6, 94) });
      } else if (d.mode === 'rotate') {
        const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI + 90;
        onPlace?.(d.id, { ...d.place, rot: Math.round(ang) });
      } else if (d.mode === 'scale') {
        const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
        onPlace?.(d.id, { ...d.place, scale: clamp(+(d.baseScale * dist / d.baseDist).toFixed(2), 0.4, 2.6) });
      }
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [editable, onPlace]);

  const startDrag = (mode, id, place, e) => {
    if (!editable || !pocketRef.current) return;
    e.stopPropagation(); e.preventDefault();
    const r = pocketRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * 100;
    const py = ((e.clientY - r.top) / r.height) * 100;
    const cx = r.left + (place.x / 100) * r.width, cy = r.top + (place.y / 100) * r.height;
    dragRef.current = {
      mode, id, place, rect: r,
      offX: place.x - px, offY: place.y - py,
      baseDist: Math.max(12, Math.hypot(e.clientX - cx, e.clientY - cy)), baseScale: place.scale,
    };
    onSelect?.(id);
  };

  useEffect(() => { if (!open) setDetail(null); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') { if (detail) setDetail(null); else setOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail]);

  return (
    <>
      <button className={`wd-bp-trigger ${variant}`} onClick={() => setOpen(true)} aria-label={label || 'Open resources'} title={label || 'Resources'}>🎒</button>

      {open && (
        <div className="wd-modal-backdrop" onClick={() => setOpen(false)}>
          <div className="wd-bp" onClick={(e) => e.stopPropagation()}>
            <div className="wd-bp-strap wd-bp-strap-l" />
            <div className="wd-bp-strap wd-bp-strap-r" />
            <div className="wd-bp-grab" />
            <button className="wd-bp-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>

            <div className="wd-bp-lid">
              <div className="wd-bp-lid-title">{editable ? 'Arrange Backpack' : 'Your Backpack'}</div>
              <div className="wd-bp-lid-sub">{editable ? 'Drag to move · corner ⤢ to scale · top ⟳ to rotate' : 'Tap an item for more information'}</div>
            </div>

            <div className={`wd-bp-pocket ${editable ? 'editing' : ''}`} ref={pocketRef}
              onPointerDown={editable ? () => onSelect?.(null) : undefined}>
              <div className="wd-bp-zip" />
              {resources.length === 0 && <div className="wd-bp-empty">Nothing packed yet.</div>}
              {resources.map((r, i) => {
                const place = placeOf(r, i);
                const isDone = !!done[r.id];
                const card = !!r.image; // full-card PNGs render as scattered cards
                const sel = editable && selectedId === r.id;
                return (
                  <div
                    key={r.id || i}
                    className={`wd-bp-place ${sel ? 'sel' : ''}`}
                    style={{ left: `${place.x}%`, top: `${place.y}%`, transform: `translate(-50%,-50%) rotate(${place.rot}deg)` }}
                  >
                    <div className="wd-bp-scale" style={{ transform: `scale(${place.scale})` }}>
                      <button
                        className={`wd-bp-obj ${card ? 'card' : 'tile'} ${isDone ? 'done' : ''} ${editable ? 'editing' : ''}`}
                        style={{ animationDelay: `${0.15 + i * 0.08}s` }}
                        onPointerDown={editable ? (e) => startDrag('move', r.id, place, e) : undefined}
                        onClick={editable ? (e) => { e.stopPropagation(); onSelect?.(r.id); } : () => setDetail(r)}
                      >
                        {!editable && isDone && <span className="wd-bp-finished"><span>✓ Finished</span></span>}
                        {card
                          ? <>
                              <img className="wd-bp-cardimg" src={picUrl(r.image)} alt={r.name || ''} style={{ animationDelay: `${(i % 5) * 0.4}s` }} />
                              <span className="wd-bp-obj-name card-name">{r.name || 'Item'}</span>
                            </>
                          : <>
                              <span className="wd-bp-obj-ic" style={{ animationDelay: `${(i % 5) * 0.4}s` }}>{r.icon || '🎒'}</span>
                              <span className="wd-bp-obj-name">{r.name || 'Item'}</span>
                            </>}
                      </button>
                    </div>
                    {sel && (
                      <>
                        <span className="wd-bp-handle rot" title="Drag to rotate"
                          style={{ transform: `translate(-50%,-50%) translateY(${-(52 * place.scale + 18)}px)` }}
                          onPointerDown={(e) => startDrag('rotate', r.id, place, e)}>⟳</span>
                        <span className="wd-bp-handle scl" title="Drag to scale"
                          style={{ transform: `translate(-50%,-50%) translate(${48 * place.scale}px, ${46 * place.scale}px)` }}
                          onPointerDown={(e) => startDrag('scale', r.id, place, e)}>⤢</span>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {detail && (
            <div className="wd-rescard-backdrop" onClick={() => setDetail(null)}>
              <div className="wd-rescard" onClick={(e) => e.stopPropagation()}>
                <button className="wd-rescard-x" onClick={() => setDetail(null)} aria-label="Close">✕</button>
                <div className="wd-rescard-icon">{detail.image ? <img src={picUrl(detail.image)} alt="" /> : (detail.icon || '🎒')}</div>
                <h3 className="wd-rescard-name">{detail.name || 'Untitled'}</h3>
                {detail.synopsis && <div className="wd-rescard-syn">{detail.synopsis}</div>}
                <div className="wd-rescard-body">
                  {detail.details ? <RichText text={detail.details} /> : 'More info coming soon.'}
                </div>
                {detail.location && (
                  <div className="wd-rescard-loc">
                    <span className="wd-rescard-loc-info">
                      <span className="wd-rescard-loc-lbl">📍 Get it at</span>
                      <span className="wd-rescard-loc-name">{detail.location}</span>
                    </span>
                    {onGo && detail.destNodeId && (
                      <button className="wd-rescard-go" onClick={() => { onGo(detail); setDetail(null); setOpen(false); }}>Go →</button>
                    )}
                  </div>
                )}
                <button
                  className={`wd-rescard-done ${done[detail.id] ? 'is-done' : ''}`}
                  onClick={() => { toggleDone(null, detail.id); setDetail(null); }}
                >{done[detail.id] ? '✓ Finished · tap to undo' : 'Done'}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
